// /settings/inventory-tracking — what Receive Goods captures per line.
//
// Owner-only. Two tenant-wide toggles (batch & expiry / serial numbers);
// individual products can override via the Tracking field on the product
// form. When both are on, serial numbers win for products that inherit.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Skeleton,
  Alert,
  Toggle,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { tenantService } from '@/services/tenant.service';
import type { InventoryTrackingSettings } from '@/services/tenant.service';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { SaveStatus } from '@/components/SaveStatus';

function effectiveLabel(s: { track_batches: boolean; track_serials: boolean }): string {
  if (s.track_serials) return 'Serial numbers';
  if (s.track_batches) return 'Batch & expiry';
  return 'Quantity only';
}

export function InventoryTrackingPage() {
  const profile = useAuthStore((s) => s.profile);
  const { toast } = useToast();
  const tenantId = profile?.tenant?.id ?? null;

  const [current, setCurrent] = useState<InventoryTrackingSettings | null>(null);
  const [draft, setDraft] = useState<{ track_batches: boolean; track_serials: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    tenantService
      .getInventoryTracking(tenantId)
      .then((res) => {
        if (cancelled) return;
        setCurrent(res);
        setDraft({ track_batches: res.track_batches, track_serials: res.track_serials });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const dirty =
    !!draft && !!current &&
    (draft.track_batches !== current.track_batches || draft.track_serials !== current.track_serials);
  const { dialog: leaveDialog } = useUnsavedChangesGuard(dirty);

  const handleSave = async () => {
    if (!tenantId || !draft || !dirty) return;
    setSaving(true);
    try {
      const saved = await tenantService.setInventoryTracking(tenantId, draft);
      setCurrent(saved);
      setDraft({ track_batches: saved.track_batches, track_serials: saved.track_serials });
      toast({ title: 'Inventory tracking updated', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer
      title="Inventory tracking"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'Inventory tracking' },
      ]}
    >
      <ErrorBoundary resetKey="inventory-tracking">
        <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-success-600 rounded-2xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">What do you record when goods arrive?</h1>
          <p className="mt-1.5 text-sm text-white/85 max-w-2xl">
            Electronics and appliances carry <strong>serial numbers</strong>; pharmacy, FMCG and kitchen
            supplies carry a <strong>batch &amp; expiry</strong>. Pick what applies to your business — Receive
            Goods asks only for that. Any product can override this from its Tracking field.
          </p>
        </div>

        {error && (
          <div className="mb-5">
            <Alert variant="danger" title="Couldn't load the current settings" description={error} />
          </div>
        )}

        {loading || !draft ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <Skeleton key={i} variant="rect" height={88} />
            ))}
          </div>
        ) : (
          <div className={cn(GLASS_FORM, 'p-5 space-y-4')}>
            {current?.is_default && (
              <Alert
                variant="info"
                title="Using the default — nothing is tracked"
                description="You haven't saved inventory settings yet. Serial / batch capture is opt-in: turn it on here for every product, or per product under Tracking on receipt."
              />
            )}

            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 hover:border-teal-300 transition-all duration-200">
              <Toggle
                id="track-serials"
                checked={draft.track_serials}
                onChange={(v) => setDraft({ ...draft, track_serials: v })}
                label="Track serial numbers"
                description="One serial per unit is captured on receipt and listed on the product's Serials tab. Best for electronics, phones, pumps, appliances."
              />
            </div>

            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 hover:border-teal-300 transition-all duration-200">
              <Toggle
                id="track-batches"
                checked={draft.track_batches}
                onChange={(v) => setDraft({ ...draft, track_batches: v })}
                label="Track batch & expiry"
                description="Batch / lot number and expiry date are captured together on receipt and feed expiry alerts. Best for pharmacy, FMCG, food ingredients."
              />
            </div>

            <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 px-4 py-3 text-sm text-teal-800 dark:text-teal-200">
              Products that inherit will ask for: <strong>{effectiveLabel(draft)}</strong>
              {draft.track_batches && draft.track_serials && ' (serial numbers take precedence; set a product to "Batch & expiry" to override)'}
              . Restaurant ingredients only ever use batch &amp; expiry.
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <SaveStatus dirty={dirty} />
              <Link to="/settings" className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300">
                Cancel
              </Link>
              <Button variant="primary" onClick={handleSave} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </ErrorBoundary>
      {leaveDialog}
    </PageContainer>
  );
}
